import { openConsentBanner } from '@ifixit/consent-banner';
import { useTranslations } from '@ifixit/i18n/hooks';
import { MenuItemType, type MenuItem, type Menu as MenuType } from '@ifixit/menu';
import { useTrackedOnClick } from '@ifixit/tracking-hooks';
import { cn } from '@ifixit/ui/lib/utils';
import { AppStoreButtons } from '@ifixit/ui/components/app-store-buttons';
import type {
   ComponentPropsWithRef,
   ElementType,
   PropsWithChildren,
   ReactElement,
   ReactNode,
   SVGProps,
} from 'react';
import type { NewsletterFormProps } from './Newsletter';
import { NewsletterComponent } from './Newsletter';

export type NavSectionProps = {
   menu1: MenuType | null;
   menu2: MenuType | null;
   menu3: MenuType | null;
   newsletterForm: NewsletterFormProps | null;
   newsletterLinkMessageSubscribed: string;
   newsletterLinkMessageUnsubscribed: string;
   storeCode: string;
   updateConsentMessage: string;
   LinkComponent: ElementType;
};

export const NavigationSection = ({
   menu1,
   menu2,
   menu3,
   newsletterForm,
   newsletterLinkMessageSubscribed,
   newsletterLinkMessageUnsubscribed,
   storeCode,
   updateConsentMessage,
   LinkComponent,
}: NavSectionProps) => (
   <FooterTopSection>
      <FooterNavigationSection>
         <FooterNavigationList>
            <NavigationColumn menu={menu1} LinkComponent={LinkComponent} />
         </FooterNavigationList>
         <FooterNavigationList>
            <NavigationColumn menu={menu2} LinkComponent={LinkComponent} />
         </FooterNavigationList>
         <FooterNavigationList>
            <NavigationColumn
               menu={menu3}
               updateConsentMessage={updateConsentMessage}
               LinkComponent={LinkComponent}
            />
         </FooterNavigationList>
         <NavigationAppStoreButtons LinkComponent={LinkComponent} />
      </FooterNavigationSection>
      {newsletterForm && (
         <NewsletterComponent
            newsletterForm={newsletterForm}
            linkMessageSubscribed={newsletterLinkMessageSubscribed}
            linkMessageUnsubscribed={newsletterLinkMessageUnsubscribed}
            storeCode={storeCode}
         />
      )}
   </FooterTopSection>
);

export const NavigationColumn = ({
   menu,
   updateConsentMessage,
   LinkComponent,
   titleClassName,
   listItemClassName,
}: {
   menu: MenuType | null;
   updateConsentMessage?: string;
   LinkComponent: ElementType;
   /** Override classes for the column title (e.g. `text-lg font-bold`). */
   titleClassName?: string;
   /** Override classes for each list-item link (e.g. `text-base`). */
   listItemClassName?: string;
}) => {
   if (!menu) {
      return null;
   }
   return (
      <>
         <NavigationTitle className={titleClassName}>{menu.title}</NavigationTitle>
         <FooterNavigationListItems
            menu={menu}
            updateConsentMessage={updateConsentMessage}
            LinkComponent={LinkComponent}
            itemClassName={listItemClassName}
         />
      </>
   );
};

function NavigationTitle({ children, className }: PropsWithChildren<{ className?: string }>) {
   return (
      <div className={cn('mb-3 mt-0 text-base font-semibold text-white', className)}>
         {children}
      </div>
   );
}

export function NavigationAppStoreButtons({
   LinkComponent,
   titleClassName,
}: {
   LinkComponent: ElementType;
   titleClassName?: string;
}) {
   const t = useTranslations('Footer');
   return (
      <FooterNavigationList>
         <NavigationTitle className={titleClassName}>
            <UnstyledFooterNavigationLink
               href="/go/app"
               LinkComponent={LinkComponent}
               className="text-white"
            >
               {t('downloadTheApp')}
            </UnstyledFooterNavigationLink>
         </NavigationTitle>
         <AppStoreButtons className="items-start sm:flex-col" buttonClassName="h-12" />
      </FooterNavigationList>
   );
}

const navListItemLinkClassName =
   'm-0 cursor-pointer p-0 text-sm font-normal text-gray-300 transition-all duration-[400ms] hover:text-white hover:no-underline [&:visited]:text-gray-300 [&:visited:hover]:text-white';

const StyledFooterListItem = ({
   children,
   href,
   LinkComponent,
   itemClassName,
   ...props
}: {
   children: ReactNode;
   href?: string;
   LinkComponent?: ElementType;
   itemClassName?: string;
} & ComponentPropsWithRef<'a'>) => (
   <FooterNavigationItem>
      <UnstyledFooterNavigationLink
         LinkComponent={LinkComponent}
         href={href}
         className={cn(navListItemLinkClassName, itemClassName)}
         {...props}
      >
         {children}
      </UnstyledFooterNavigationLink>
   </FooterNavigationItem>
);

export const FooterNavigationListItems = ({
   menu,
   updateConsentMessage,
   LinkComponent,
   itemClassName,
}: {
   menu: MenuType;
   updateConsentMessage?: string;
   LinkComponent: ElementType;
   itemClassName?: string;
}) => {
   const listItems = menu.items.map((item: MenuItem, index: number) => {
      switch (item.type) {
         case MenuItemType.Link: {
            return (
               <StyledFooterListItem
                  key={index}
                  href={item.url}
                  LinkComponent={LinkComponent}
                  itemClassName={itemClassName}
               >
                  {item.name}
               </StyledFooterListItem>
            );
         }
         default: {
            return null;
         }
      }
   });

   return (
      <>
         {listItems}
         {updateConsentMessage && (
            <UpdateConsentButton
               buttonText={updateConsentMessage}
               LinkComponent={LinkComponent}
               itemClassName={itemClassName}
            />
         )}
      </>
   );
};

function UpdateConsentButton({
   buttonText,
   LinkComponent: _LinkComponent,
   itemClassName,
}: {
   buttonText: string;
   LinkComponent: ElementType;
   itemClassName?: string;
}) {
   return (
      <StyledFooterListItem
         data-testid="footer-cookie-consent"
         onClick={() => openConsentBanner({ showDetails: true })}
         itemClassName={itemClassName}
      >
         {buttonText}
      </StyledFooterListItem>
   );
}

export function FooterTopSection({ ref, className, ...props }: ComponentPropsWithRef<'div'>) {
   return (
      <div
         ref={ref}
         className={cn('flex flex-col flex-wrap justify-between pb-10 md:flex-row', className)}
         {...props}
      />
   );
}

export function FooterNavigationSection({ children }: PropsWithChildren<object>) {
   return (
      <div className="grid flex-1 grid-cols-2 gap-4 p-0 sm:grid-cols-4 md:mr-24">{children}</div>
   );
}

export function FooterNavigationList({
   ref,
   children,
   className,
   ...otherProps
}: ComponentPropsWithRef<'ul'>) {
   return (
      <ul ref={ref} className={cn('m-0 list-none pb-4 pl-0 sm:pb-0', className)} {...otherProps}>
         {children}
      </ul>
   );
}

export const FooterNavigationItem = ({ className, ...props }: ComponentPropsWithRef<'li'>) => (
   <li className={cn('my-1 py-1 text-sm text-gray-300', className)} {...props} />
);

type FooterNavigationLinkProps = Omit<ComponentPropsWithRef<'a'>, 'ref'> & {
   LinkComponent?: ElementType;
   icon?: (props: SVGProps<SVGSVGElement>) => ReactElement;
   href?: string;
   ref?: ComponentPropsWithRef<'a'>['ref'];
};

export function FooterNavigationLink({ className, ...otherProps }: FooterNavigationLinkProps) {
   return (
      <UnstyledFooterNavigationLink
         className={cn(navListItemLinkClassName, className)}
         {...otherProps}
      />
   );
}

function UnstyledFooterNavigationLink({
   ref,
   LinkComponent: Component = 'a',
   icon: Icon,
   children,
   className,
   ...otherProps
}: FooterNavigationLinkProps) {
   const trackedOnClick = useTrackedOnClick({
      clickName: children?.toString(),
      ...otherProps,
   });
   return (
      <Component ref={ref} className={className} onClick={trackedOnClick} {...otherProps}>
         {Icon && <Icon className="mr-1 inline size-4 opacity-50" />}
         {children}
      </Component>
   );
}
